import { ChangeEvent, FormEvent } from "react";

import { FileInput } from "@ezmid/components-library";
import {
  Control,
  FieldErrors,
  FieldValues,
  useWatch,
  FieldPath,
  Path,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { EntityMedia } from "../../../api/lesson.generated";
import { EntityLessonContentType } from "../../../api/lessons-create-content.generated";
import { FormInputField } from "../../../components/hookFormComponents/FormInputField";
import { MarkdownField } from "../../../components/hookFormComponents/MarkdownField";
import { SelectInputField } from "../../../components/hookFormComponents/SelectInputField";
import { MediaPreview } from "../../../components/media/MediaPreview";
import { Tabs } from "../../../components/tabs/Tabs";
import { Optional } from "../../../enums/types";
import { MediaData } from "../../../hooks/useGetMedia";

interface LessonContentFields extends FieldValues {
  lessonContentType?: string;
  nameDE?: string;
  nameEN?: string;
  textDE?: string;
  textEN?: string;
  order?: number | string;
  status?: string;
  notePlaceholder?: string;
  scaleContent?: string;
  scaleMinValue?: number | string;
  scaleMinLabelDE?: string;
  scaleMinLabelEN?: string;
  scaleMaxValue?: number | string;
  scaleMaxLabelDE?: string;
  scaleMaxLabelEN?: string;
}

type Props<T extends LessonContentFields> = {
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  control: Control<T>;
  errors: FieldErrors<T>;
  onMediaFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  uploadedMedia?: Optional<MediaData>;
  isMediaUploading?: boolean;
  selectedFileName?: string;
  existingMedia?: EntityMedia;
  onMediaDelete?: () => void;
};

export function LessonContentForm<T extends LessonContentFields>({
  handleSubmit,
  control,
  errors,
  onMediaFileChange,
  uploadedMedia,
  isMediaUploading = false,
  selectedFileName,
  existingMedia,
  onMediaDelete,
}: Props<T>) {
  const { t } = useTranslation();

  const lessonContentType = useWatch({
    control,
    name: "lessonContentType" as Path<T>,
  }) as EntityLessonContentType;

  const contentTypeOptions = [
    { value: "lct-theory", label: "Theory" },
    { value: "lct-experiment", label: "Experiment" },
    { value: "lct-reminder", label: "Reminder" },
    { value: "lct-scale", label: "Scale" },
  ];

  const statusOptions = [
    { value: "lcs-active", label: t("status.active") },
    { value: "lcs-preview", label: t("status.preview") },
    { value: "lcs-draft", label: t("status.draft") },
  ];

  const nameDataTabs = [
    {
      label: `${t("form.name.label")} DE`,
      content: (
        <FormInputField
          key="nameDE"
          name={"nameDE" as Path<T>}
          htmlFor="nameDE"
          id="nameDE"
          type="text"
          control={control}
          error={(errors as any).nameDE}
          placeholder={t("form.name.label")}
        />
      ),
    },
    {
      label: `${t("form.name.label")} EN`,
      content: (
        <FormInputField
          key="nameEN"
          name={"nameEN" as Path<T>}
          htmlFor="nameEN"
          id="nameEN"
          type="text"
          control={control}
          error={(errors as any).nameEN}
          placeholder={t("form.name.label")}
        />
      ),
    },
  ];

  const textDataTabs = [
    {
      label: `${t("app.lesson.text.label")} DE`,
      content: (
        <MarkdownField
          key="textDE"
          name={"textDE" as FieldPath<T>}
          htmlFor="textDE"
          id="textDE"
          control={control}
          error={(errors as any).textDE}
        />
      ),
    },
    {
      label: `${t("app.lesson.text.label")} EN`,
      content: (
        <MarkdownField
          key="textEN"
          name={"textEN" as FieldPath<T>}
          htmlFor="textEN"
          id="textEN"
          control={control}
          error={(errors as any).textEN}
        />
      ),
    },
  ];

  const scaleMinLabelTabs = [
    {
      label: "Min Label DE",
      content: (
        <MarkdownField
          key="scaleMinLabelDE"
          name={"scaleMinLabelDE" as FieldPath<T>}
          htmlFor="scaleMinLabelDE"
          id="scaleMinLabelDE"
          control={control}
          error={(errors as any).scaleMinLabelDE}
        />
      ),
    },
    {
      label: "Min Label EN",
      content: (
        <MarkdownField
          key="scaleMinLabelEN"
          name={"scaleMinLabelEN" as FieldPath<T>}
          htmlFor="scaleMinLabelEN"
          id="scaleMinLabelEN"
          control={control}
          error={(errors as any).scaleMinLabelEN}
        />
      ),
    },
  ];

  const scaleMaxLabelTabs = [
    {
      label: "Max Label DE",
      content: (
        <MarkdownField
          key="scaleMaxLabelDE"
          name={"scaleMaxLabelDE" as FieldPath<T>}
          htmlFor="scaleMaxLabelDE"
          id="scaleMaxLabelDE"
          control={control}
          error={(errors as any).scaleMaxLabelDE}
        />
      ),
    },
    {
      label: "Max Label EN",
      content: (
        <MarkdownField
          key="scaleMaxLabelEN"
          name={"scaleMaxLabelEN" as FieldPath<T>}
          htmlFor="scaleMaxLabelEN"
          id="scaleMaxLabelEN"
          control={control}
          error={(errors as any).scaleMaxLabelEN}
        />
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 gap-4">
        <div className="row-span-2 col-span-2 space-y-2">
          <Tabs tabsData={nameDataTabs} />
          <SelectInputField
            label="Content Type"
            name={"lessonContentType" as Path<T>}
            htmlFor="lessonContentType"
            control={control}
            options={contentTypeOptions}
            defaultValue={contentTypeOptions[0]}
          />

          <SelectInputField
            label={t("form.status.label")}
            name={"status" as Path<T>}
            htmlFor="status"
            control={control}
            options={statusOptions}
            defaultValue={statusOptions[2]}
          />

          <div className="flex flex-col">
            <label
              className="block font-normal tmb-1 text-gray-500"
              htmlFor="media"
            >
              {t("form.label.upload.content")}
            </label>
            <FileInput
              inputSize="md"
              id="media"
              onChange={onMediaFileChange}
              accept="image/*,video/*,audio/*"
              key={selectedFileName || "file-input"}
            />
            {isMediaUploading && (
              <p className="mt-2 text-sm text-gray-600">
                {t("app.media.uploading")}...
              </p>
            )}
            {uploadedMedia && !isMediaUploading && (
              <MediaPreview
                url={uploadedMedia.url}
                type={uploadedMedia.type}
                fileName={selectedFileName || uploadedMedia.key}
                thumbnailUrl={uploadedMedia.thumbnailUrl}
                cookies={uploadedMedia.cookies}
                onDelete={onMediaDelete}
              />
            )}
            {!uploadedMedia && !isMediaUploading && existingMedia && (
              <MediaPreview
                url={
                  existingMedia.type === "image"
                    ? existingMedia.image?.content?.find(
                        (img) => img.type === "square-large"
                      )?.url ||
                      existingMedia.image?.content?.[0]?.url ||
                      ""
                    : existingMedia.type === "video"
                    ? existingMedia.video?.content?.url || ""
                    : existingMedia.audio?.content?.url || ""
                }
                type={existingMedia.type || "image"}
                fileName={existingMedia.key}
                thumbnailUrl={
                  existingMedia.type === "video"
                    ? existingMedia.video?.content?.thumbnail?.find(
                        (thumb) => thumb.type === "square-large"
                      )?.url
                    : undefined
                }
                cookies={(() => {
                  const mediaCookies =
                    existingMedia.type === "video"
                      ? existingMedia.video?.content?.cookies
                      : existingMedia.type === "audio"
                      ? existingMedia.audio?.content?.cookies
                      : undefined;
                  // eslint-disable-next-line no-console
                  console.log("Media cookies from API:", mediaCookies);
                  // Check if cookies is an array or need to be converted
                  if (!mediaCookies || mediaCookies.length === 0) {
                    return undefined;
                  }
                  return mediaCookies.map((c) => ({
                    name: c.name || "",
                    value: c.value || "",
                  }));
                })()}
                onDelete={onMediaDelete}
              />
            )}
          </div>

          <FormInputField
            name={"order" as Path<T>}
            htmlFor="order"
            label="Order"
            id="order"
            type="number"
            control={control}
            error={(errors as any).order}
            placeholder="Order"
          />

          <Tabs tabsData={textDataTabs} />

          {lessonContentType === "lct-reminder" && (
            <div className="">
              <h4 className="font-medium">Note Metadata</h4>
              <FormInputField
                name={"notePlaceholder" as Path<T>}
                htmlFor="notePlaceholder"
                label="Placeholder"
                id="notePlaceholder"
                type="text"
                control={control}
                error={(errors as any).notePlaceholder}
              />
            </div>
          )}

          {lessonContentType === "lct-scale" && (
            <div className="">
              <h4 className="font-medium">Scale Metadata</h4>
              <MarkdownField
                name={"scaleContent" as Path<T>}
                htmlFor="scaleContent"
                label="Content"
                id="scaleContent"
                control={control}
                error={(errors as any).scaleContent}
              />
              <FormInputField
                name={"scaleMinValue" as Path<T>}
                htmlFor="scaleMinValue"
                label="Min Value"
                id="scaleMinValue"
                type="number"
                control={control}
                error={(errors as any).scaleMinValue}
              />
              <Tabs tabsData={scaleMinLabelTabs} />
              <FormInputField
                name={"scaleMaxValue" as Path<T>}
                htmlFor="scaleMaxValue"
                label="Max Value"
                id="scaleMaxValue"
                type="number"
                control={control}
                error={(errors as any).scaleMaxValue}
              />
              <Tabs tabsData={scaleMaxLabelTabs} />
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
